/**
 * <garment-spec>
 *
 * Reveals the "Fabric & Fit" panel and the size-chart table belonging to the
 * currently-selected Shirt Style. Every garment's panel is already in the DOM
 * (rendered by snippets/garment-spec.liquid and garment-size-chart-modal.liquid);
 * this only toggles [hidden].
 *
 * Three things make this less trivial than it looks:
 *
 * 1. WE CANNOT USE PUB_SUB_EVENTS.variantChange.
 *    product-info.js:173 does `if (!variant) { this.setUnavailable(); return; }`
 *    BEFORE it publishes variantChange at :204. Our size grid is deliberately sparse
 *    (S is stocked on the DryBlend blank only; the black hoodie is M/L/2XL only), so
 *    "the selected style+size resolves to no variant" is a COMMON state -- and it is
 *    precisely the state the panel must survive. So we listen to
 *    optionValueSelectionChange, which VariantSelects publishes on every option click
 *    regardless of whether a variant resolves. (variantChange is also subscribed, as
 *    a cheap re-sync after the section re-render, but it is never load-bearing.)
 *
 * 2. WE CANNOT READ THE STYLE OFF THE RESOLVED VARIANT.
 *    Same reason. `[data-selected-variant]` is null on an unavailable combination.
 *    The truth is the CHECKED RADIO / SELECTED OPTION in the picker, so read that.
 *
 * 3. DAWN REPLACES THE ENTIRE <variant-selects> NODE ON EVERY CHANGE.
 *    HTMLUpdateUtility.viewTransition (global.js) inserts a fresh node and removes the
 *    old one, so any listener bound to the picker or its inputs is destroyed. We bind a
 *    DELEGATED listener to <product-info>, which is not replaced for a same-product
 *    change, and which the bubbled `change` still reaches.
 *
 * The size-chart modal is NOT inside this element: ModalDialog.connectedCallback moves
 * itself to document.body. Reach it with document.getElementById().
 */
if (!customElements.get('garment-spec')) {
  customElements.define(
    'garment-spec',
    class GarmentSpec extends HTMLElement {
      constructor() {
        super();
        this.onChange = this.onChange.bind(this);
      }

      connectedCallback() {
        this.productInfo = this.closest('product-info');
        this.sectionId = this.dataset.sectionId;
        this.optionName = this.dataset.styleOptionName;

        // Dawn names its option inputs deterministically (product-variant-options.liquid):
        //   radio / swatch : name="{{ option.name }}-{{ option.position }}"
        //   dropdown       : name="options[{{ option.name }}]"
        // We match on the name PROPERTY rather than a CSS attribute selector, so
        // nothing has to be escaped -- which matters, because these labels contain an
        // EN DASH (U+2013).
        //
        // Dawn builds the radio name with {%- capture -%}, and that capture keeps a
        // TRAILING NEWLINE: the live attribute is "Shirt Style-1\n", not "Shirt Style-1".
        // A strict === against the untrimmed value silently never matches -- no error,
        // the panel just never moves. Always compare through normalizeName().
        this.inputName = `${this.optionName}-${this.dataset.styleOptionPosition}`;
        this.selectName = `options[${this.optionName}]`;

        this.listenTarget = this.productInfo || document;
        this.listenTarget.addEventListener('change', this.onChange);

        this.optionChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, ({ data }) => {
          if (this.productInfo && data?.event?.target && !this.productInfo.contains(data.event.target)) return;
          this.update();
        });

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (data.sectionId !== this.sectionId) return;
          this.update();
        });

        this.update();
      }

      disconnectedCallback() {
        this.listenTarget?.removeEventListener('change', this.onChange);
        this.optionChangeUnsubscriber?.();
        this.variantChangeUnsubscriber?.();
      }

      /** Dawn's {%- capture -%} leaves a trailing newline in the radio's name. */
      normalizeName(element) {
        return (element.name || '').trim();
      }

      isStyleInput(element) {
        const name = this.normalizeName(element);
        return name === this.inputName || name === this.selectName;
      }

      onChange(event) {
        if (event.target && this.isStyleInput(event.target)) this.update();
      }

      /**
       * The selected Shirt Style, straight out of the picker.
       * Never derived from the resolved variant -- see note 2 above.
       */
      get selectedStyle() {
        const root = this.productInfo || document;
        const picker = root.querySelector('variant-selects');
        if (!picker) return null;

        const select = Array.from(picker.querySelectorAll('select')).find((el) => this.isStyleInput(el));
        if (select) return select.value;

        const radio = Array.from(picker.querySelectorAll('input[type="radio"]:checked')).find((el) =>
          this.isStyleInput(el)
        );
        return radio ? radio.value : null;
      }

      get modal() {
        return document.getElementById(`GarmentSizeChart-${this.sectionId}`);
      }

      update() {
        const style = this.selectedStyle;
        if (!style) return;

        this.reveal(this, style);

        const modal = this.modal;
        if (modal) this.reveal(modal, style);
      }

      reveal(root, style) {
        root.querySelectorAll('[data-garment-style]').forEach((panel) => {
          panel.hidden = panel.dataset.garmentStyle !== style;
        });
      }
    }
  );
}
